import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CalendarClock,
  CheckCircle2,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  SWITCH_DISPLAY_STATUS_LABELS,
  ANALYZE_END_OF_CALL_REPORT_KEY,
  CONTACT_INTELLIGENCE_KEY,
  CONTACT_INTELLIGENCE_TRIGGER_OPTIONS,
  INCOMING_CALL_TRIAGE_KEY,
  INTAKE_LEAD_GENERATION_KEY,
  INTAKE_LEAD_GENERATION_MODELS,
  INTELLIGENT_FORM_TRIAGE_KEY,
  LOW_VALUE_CALL_PURPOSE_KEYS,
  normalizeContactIntelligenceSettings,
  normalizeIncomingCallTriageSettings,
  normalizeIntakeLeadGenerationSettings,
  type AutomatedFormFollowUpCallSettings,
  type ContactIntelligenceSettings,
  type AnalyzeEndOfCallSettings,
  type IncomingCallTriageSettings,
  type IntakeLeadGenerationSettings,
  type AvailabilityWindow,
  type PortalSwitchSummary,
  type PortalUserOption,
  type QualificationQuestion,
  type SwitchConfigurationIssue,
} from '@command-center/switchboard-sdk';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@command-center/ui-system';
import { switchboardApi } from '../lib/switchboard-api';
import { aiSystemsApi } from '../lib/ai-systems-api';

type SettingsTab =
  | 'general'
  | 'trigger'
  | 'calling'
  | 'receptionist'
  | 'scheduling'
  | 'notifications'
  | 'field_mapping';
type SettingsGroup = Exclude<keyof AutomatedFormFollowUpCallSettings, 'fieldMappings'>;

const QUERY_KEY = ['cascade', 'switchboard'] as const;
const TEXTAREA_CLASS =
  'flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

interface SwitchSettingsProps {
  item: PortalSwitchSummary;
  users: PortalUserOption[];
  canManage: boolean;
  executionReady: boolean;
  onBack: () => void;
  onToggle: (enabled: boolean) => void;
  togglePending: boolean;
}

export function SwitchSettingsView(props: SwitchSettingsProps) {
  if (props.item.definition.key === INTAKE_LEAD_GENERATION_KEY) {
    return <IntakeLeadGenerationSettingsView {...props} />;
  }
  if (props.item.definition.key === CONTACT_INTELLIGENCE_KEY) {
    return <ContactIntelligenceSettingsView {...props} />;
  }
  if (props.item.definition.key === INCOMING_CALL_TRIAGE_KEY) {
    return <IncomingCallTriageSettingsView {...props} />;
  }
  if (props.item.definition.key === ANALYZE_END_OF_CALL_REPORT_KEY) {
    return <AnalyzeEndOfCallSettingsView {...props} />;
  }
  if (props.item.definition.key === INTELLIGENT_FORM_TRIAGE_KEY) {
    return <IntelligentFormTriageView {...props} />;
  }
  return <AutomatedSwitchSettingsView {...props} item={props.item as PortalSwitchSummary & { settings: AutomatedFormFollowUpCallSettings }} />;
}

function IntakeLeadGenerationSettingsView({
  item,
  canManage,
  onBack,
}: SwitchSettingsProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<IntakeLeadGenerationSettings>(() =>
    normalizeIntakeLeadGenerationSettings(item.settings));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(normalizeIntakeLeadGenerationSettings(item.settings));
    setSaved(false);
  }, [item.id, item.settings]);

  const save = useMutation({
    mutationFn: () => switchboardApi.saveSettings(item.definition.key, draft),
    onSuccess: async ({ item: updated }) => {
      setDraft(normalizeIntakeLeadGenerationSettings(updated.settings));
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const numeric = (key: Exclude<keyof IntakeLeadGenerationSettings, 'model'>) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setSaved(false);
      setDraft((current) => ({ ...current, [key]: Number(event.target.value) }));
    };

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" className="-ml-3 mb-2" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Back to Switchboard
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-semibold text-foreground">{item.definition.name}</h2>
          <Badge variant="success">Included · Active</Badge>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{item.definition.description}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryMetric label="Executions this month" value={item.usage.executions} />
        <SummaryMetric label="Completed" value={item.usage.completed} />
        <SummaryMetric label="Failed" value={item.usage.failed} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Guardrails</CardTitle>
          <p className="text-sm text-muted-foreground">
            These limits apply to single-record and bulk Lead creation. Identity fields from the source are never replaced by model output.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1.5 text-sm">
            <span className="font-medium">Model</span>
            <select
              className="h-9 w-full rounded-md border border-border bg-card px-2 text-sm text-foreground"
              value={draft.model}
              onChange={(event) => {
                setSaved(false);
                setDraft((current) => ({ ...current, model: event.target.value }));
              }}
            >
              {INTAKE_LEAD_GENERATION_MODELS.map((model) => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium">Daily budget (USD)</span>
            <Input type="number" min="0.01" max="1000" step="0.01" value={draft.dailyBudgetUsd} onChange={numeric('dailyBudgetUsd')} />
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium">Maximum bulk size</span>
            <Input type="number" min="1" max="50" value={draft.maxBatchSize} onChange={numeric('maxBatchSize')} />
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium">Source character limit</span>
            <Input type="number" min="500" max="20000" step="500" value={draft.maxSourceCharacters} onChange={numeric('maxSourceCharacters')} />
          </label>
          <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
            <div className="text-sm">
              {saved && <span className="text-success">Guardrails saved.</span>}
              {save.error && <span className="text-destructive">{save.error instanceof Error ? save.error.message : 'Settings could not be saved.'}</span>}
            </div>
            <Button disabled={!canManage || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save guardrails'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ContactIntelligenceSettingsView({
  item,
  canManage,
  onBack,
  onToggle,
  togglePending,
}: SwitchSettingsProps) {
  const queryClient = useQueryClient();
  const normalized = normalizeContactIntelligenceSettings(item.settings);
  const [draft, setDraft] = useState<ContactIntelligenceSettings>(() => structuredClone(normalized));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(structuredClone(normalizeContactIntelligenceSettings(item.settings)));
    setSaved(false);
  }, [item.id, item.settings]);

  const save = useMutation({
    mutationFn: () => switchboardApi.saveSettings(item.definition.key, draft),
    onSuccess: async ({ item: updated }) => {
      setDraft(structuredClone(normalizeContactIntelligenceSettings(updated.settings)));
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button variant="ghost" size="sm" className="-ml-3 mb-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" /> Back to Switchboard
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground">{item.definition.name}</h2>
            <Badge variant={item.displayStatus === 'active' ? 'success' : 'warning'}>
              {SWITCH_DISPLAY_STATUS_LABELS[item.displayStatus]}
            </Badge>
          </div>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            {item.definition.description}
          </p>
        </div>
        <Card className="min-w-[250px]">
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div>
              <div className="text-sm font-medium text-foreground">Switch enabled</div>
              <div className="text-xs text-muted-foreground">
                {item.enabled ? 'Contact overview updates are allowed.' : 'All updates are paused.'}
              </div>
            </div>
            <Switch
              checked={item.enabled}
              disabled={!canManage || togglePending}
              onCheckedChange={onToggle}
              aria-label="Enable Contact Intelligence"
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryMetric label="Summary updates this month" value={item.usage.summaryUpdates ?? 0} />
        <SummaryMetric label="Executions" value={item.usage.executions} />
        <SummaryMetric label="Failed" value={item.usage.failed} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Update triggers</CardTitle>
          <p className="text-sm text-muted-foreground">
            Choose the meaningful Contact events that may enqueue an AI overview refresh. Manual
            refresh remains available whenever the Switch is enabled.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {CONTACT_INTELLIGENCE_TRIGGER_OPTIONS.map((option) => (
            <label
              key={option.key}
              className="flex items-start justify-between gap-4 rounded-lg border border-border p-3"
            >
              <span>
                <span className="block text-sm font-medium text-foreground">{option.label}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{option.description}</span>
              </span>
              <Switch
                checked={draft.triggers[option.key]}
                disabled={!canManage || save.isPending}
                onCheckedChange={(checked) => {
                  setSaved(false);
                  setDraft((current) => ({
                    triggers: { ...current.triggers, [option.key]: checked },
                  }));
                }}
                aria-label={option.label}
              />
            </label>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="text-sm">
              {saved && <span className="text-success">Trigger settings saved.</span>}
              {save.error && (
                <span className="text-destructive">
                  {save.error instanceof Error ? save.error.message : 'Settings could not be saved.'}
                </span>
              )}
            </div>
            <Button disabled={!canManage || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save trigger settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AutomatedSwitchSettingsView({
  item,
  users,
  canManage,
  executionReady,
  onBack,
  onToggle,
  togglePending,
}: Omit<SwitchSettingsProps, 'item'> & {
  item: PortalSwitchSummary & { settings: AutomatedFormFollowUpCallSettings };
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<AutomatedFormFollowUpCallSettings>(() => structuredClone(item.settings));
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    issueTab(item.configurationIssues[0]) ?? 'general',
  );
  const [saved, setSaved] = useState(false);
  const agentsQuery = useQuery({
    queryKey: ['cascade', 'ai-systems', 'agents', 'switchboard-options'],
    queryFn: () => aiSystemsApi.agents(),
    staleTime: 60_000,
  });

  useEffect(() => {
    setDraft(structuredClone(item.settings));
  }, [item.id, item.settings]);

  const saveMutation = useMutation({
    mutationFn: () => switchboardApi.saveSettings(item.definition.key, draft),
    onSuccess: async ({ item: updated }) => {
      setDraft(structuredClone(updated.settings as AutomatedFormFollowUpCallSettings));
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  function updateGroup<K extends SettingsGroup>(
    group: K,
    patch: Partial<AutomatedFormFollowUpCallSettings[K]>,
  ) {
    setSaved(false);
    setDraft((current) => ({
      ...current,
      [group]: { ...current[group], ...patch },
    }));
  }

  function selectAssignedUser(id: string) {
    const user = users.find((candidate) => candidate.id === id);
    if (!user) return;
    setDraft((current) => ({
      ...current,
      general: {
        ...current.general,
        assignedPortalUserId: user.id,
        assignedPortalUserName: user.name,
        assignedPortalUserEmail: user.email,
      },
      notifications: { ...current.notifications, email: user.email },
    }));
    setSaved(false);
  }

  const errorMessage = saveMutation.error instanceof Error ? saveMutation.error.message : undefined;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button variant="ghost" size="sm" className="-ml-3 mb-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" /> Back to Switchboard
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground">{item.definition.name}</h2>
            <Badge
              variant={
                item.displayStatus === 'active'
                  ? 'success'
                  : item.displayStatus === 'error'
                    ? 'destructive'
                    : 'warning'
              }
            >
              {SWITCH_DISPLAY_STATUS_LABELS[item.displayStatus]}
            </Badge>
          </div>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            {item.definition.description}
          </p>
        </div>
        <Card className="min-w-[250px]">
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div>
              <div className="text-sm font-medium text-foreground">Switch enabled</div>
              <div className="text-xs text-muted-foreground">
                {item.enabled
                  ? 'New executions are allowed.'
                  : 'Settings and history are preserved.'}
              </div>
            </div>
            <Switch
              checked={item.enabled}
              disabled={!canManage || togglePending}
              onCheckedChange={onToggle}
              aria-label="Enable Automated Form Follow-Up Call"
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryMetric label="Calls this month" value={item.usage.estimatedUsage.calls} />
        <SummaryMetric label="Completed" value={item.usage.completed} />
        <SummaryMetric label="Qualified leads" value={item.usage.qualified} />
        <SummaryMetric label="Appointments" value={item.usage.appointmentsScheduled} />
      </div>

      {item.configurationIssues.length > 0 && (
        <Card className="border-warning/40 bg-warning/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 text-warning" /> Setup required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {item.configurationIssues.map((issue) => {
                const tab = issueTab(issue);
                return (
                  <button
                    key={issue.code}
                    type="button"
                    className="rounded-md border border-warning/30 bg-background px-2.5 py-1.5 text-left text-xs text-foreground hover:border-warning"
                    onClick={() => tab && setActiveTab(tab)}
                  >
                    {issue.message}
                  </button>
                );
              })}
            </div>
            {!executionReady && (
              <p className="mt-3 text-xs text-muted-foreground">
                One Up must verify both the form trigger and call provider before this Switch can
                activate. Saving client settings does not place calls.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4">
          <fieldset disabled={!canManage}>
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as SettingsTab)}>
              <TabsList className="flex h-auto flex-wrap justify-start gap-1">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="trigger">Trigger</TabsTrigger>
                <TabsTrigger value="calling">Calling</TabsTrigger>
                <TabsTrigger value="receptionist">Receptionist</TabsTrigger>
                <TabsTrigger value="scheduling">Scheduling</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                <TabsTrigger value="field_mapping">Field mapping</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="mt-5">
                <Section
                  title="General"
                  description="Name the workflow and choose its owner and timezone."
                >
                  <Field label="Workflow name">
                    <Input
                      value={draft.general.workflowName}
                      onChange={(event) =>
                        updateGroup('general', { workflowName: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Timezone">
                    <Input
                      value={draft.general.timezone}
                      placeholder="America/Los_Angeles"
                      onChange={(event) => updateGroup('general', { timezone: event.target.value })}
                    />
                  </Field>
                  <Field label="Assigned portal user" className="md:col-span-2">
                    {users.length > 0 ? (
                      <Select
                        value={draft.general.assignedPortalUserId || undefined}
                        onValueChange={selectAssignedUser}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a portal user" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} · {user.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-sm text-muted-foreground">
                        No active Cascade portal users are available. Add a client membership first.
                      </div>
                    )}
                  </Field>
                  <Field label="Internal description" className="md:col-span-2">
                    <textarea
                      className={TEXTAREA_CLASS}
                      value={draft.general.internalDescription}
                      onChange={(event) =>
                        updateGroup('general', { internalDescription: event.target.value })
                      }
                    />
                  </Field>
                </Section>
              </TabsContent>

              <TabsContent value="trigger" className="mt-5">
                <Section
                  title="Form trigger"
                  description="Identify the real form submission that should start this workflow."
                >
                  <Field label="Form source">
                    <Input
                      value={draft.trigger.formSource}
                      placeholder="Cascade website / Form_Entries"
                      onChange={(event) =>
                        updateGroup('trigger', { formSource: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Form name">
                    <Input
                      value={draft.trigger.formName}
                      placeholder="Contact form"
                      onChange={(event) => updateGroup('trigger', { formName: event.target.value })}
                    />
                  </Field>
                  <Field label="Form identifier">
                    <Input
                      value={draft.trigger.formIdentifier}
                      placeholder="Stable form id or slug"
                      onChange={(event) =>
                        updateGroup('trigger', { formIdentifier: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Trigger delay (minutes)">
                    <Input
                      type="number"
                      min={0}
                      max={60}
                      value={draft.trigger.triggerDelayMinutes}
                      onChange={(event) =>
                        updateGroup('trigger', {
                          triggerDelayMinutes: numberInput(event.target.value, 0),
                        })
                      }
                    />
                  </Field>
                  <Field
                    label="Eligible submission types"
                    hint="Comma-separated; leave blank to accept all eligible entries."
                    className="md:col-span-2"
                  >
                    <Input
                      value={draft.trigger.eligibleSubmissionTypes.join(', ')}
                      onChange={(event) =>
                        updateGroup('trigger', {
                          eligibleSubmissionTypes: commaList(event.target.value),
                        })
                      }
                    />
                  </Field>
                </Section>
              </TabsContent>

              <TabsContent value="calling" className="mt-5">
                <Section
                  title="Calling behavior"
                  description="Safe retry and business-hour limits for outbound calls."
                >
                  <ToggleField
                    label="Call immediately after submission"
                    description="Otherwise wait for the configured delay."
                    checked={draft.calling.callImmediately}
                    onChange={(checked) => updateGroup('calling', { callImmediately: checked })}
                  />
                  <ToggleField
                    label="Do not call outside business hours"
                    description="Queue the next attempt until the allowed window."
                    checked={draft.calling.doNotCallOutsideBusinessHours}
                    onChange={(checked) =>
                      updateGroup('calling', { doNotCallOutsideBusinessHours: checked })
                    }
                  />
                  <Field label="Delay before call (minutes)">
                    <Input
                      type="number"
                      min={0}
                      max={60}
                      value={draft.calling.delayBeforeCallMinutes}
                      onChange={(event) =>
                        updateGroup('calling', {
                          delayBeforeCallMinutes: numberInput(event.target.value, 0),
                        })
                      }
                    />
                  </Field>
                  <Field label="Maximum call attempts" hint="Limited to three attempts.">
                    <Input
                      type="number"
                      min={1}
                      max={3}
                      value={draft.calling.maximumAttempts}
                      onChange={(event) =>
                        updateGroup('calling', {
                          maximumAttempts: numberInput(event.target.value, 1),
                        })
                      }
                    />
                  </Field>
                  <Field
                    label="Delay between attempts (minutes)"
                    hint="Default is four business hours."
                  >
                    <Input
                      type="number"
                      min={60}
                      value={draft.calling.retryDelayMinutes}
                      onChange={(event) =>
                        updateGroup('calling', {
                          retryDelayMinutes: numberInput(event.target.value, 60),
                        })
                      }
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Allowed from">
                      <Input
                        type="time"
                        value={draft.calling.allowedStartTime}
                        onChange={(event) =>
                          updateGroup('calling', { allowedStartTime: event.target.value })
                        }
                      />
                    </Field>
                    <Field label="Allowed until">
                      <Input
                        type="time"
                        value={draft.calling.allowedEndTime}
                        onChange={(event) =>
                          updateGroup('calling', { allowedEndTime: event.target.value })
                        }
                      />
                    </Field>
                  </div>
                </Section>
              </TabsContent>

              <TabsContent value="receptionist" className="mt-5">
                <Section
                  title="Receptionist behavior"
                  description="Define the voice assistant's client-safe conversation goals."
                >
                  <Field
                    label="Receptionist or assistant"
                    className="md:col-span-2"
                    hint="Use the friendly name assigned by One Up; provider credentials are never shown here."
                  >
                    {agentsQuery.data?.items.length ? (
                      <Select
                        value={draft.receptionist.receptionistId || undefined}
                        onValueChange={(value) => {
                          const agent = agentsQuery.data?.items.find(
                            (candidate) => candidate.id === value,
                          );
                          updateGroup('receptionist', {
                            receptionistId: value,
                            receptionistName: agent?.name ?? '',
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a receptionist" />
                        </SelectTrigger>
                        <SelectContent>
                          {agentsQuery.data.items.map((agent) => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name}
                              {agent.connectionStatus ? ` · ${agent.connectionStatus}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-sm text-muted-foreground">
                        {agentsQuery.isLoading
                          ? 'Loading available receptionists…'
                          : 'No Cascade receptionist is available. One Up must connect one before activation.'}
                      </div>
                    )}
                  </Field>
                  <Field label="Opening message" className="md:col-span-2">
                    <textarea
                      className={TEXTAREA_CLASS}
                      value={draft.receptionist.openingMessage}
                      onChange={(event) =>
                        updateGroup('receptionist', { openingMessage: event.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="Call objectives"
                    className="md:col-span-2"
                    hint="One objective per line."
                  >
                    <textarea
                      className={TEXTAREA_CLASS}
                      value={draft.receptionist.objectives.join('\n')}
                      onChange={(event) =>
                        updateGroup('receptionist', { objectives: lineList(event.target.value) })
                      }
                    />
                  </Field>
                  <Field label="Closing behavior" className="md:col-span-2">
                    <textarea
                      className={TEXTAREA_CLASS}
                      value={draft.receptionist.closingBehavior}
                      onChange={(event) =>
                        updateGroup('receptionist', { closingBehavior: event.target.value })
                      }
                    />
                  </Field>
                  <div className="space-y-3 md:col-span-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">Qualification questions</div>
                        <div className="text-xs text-muted-foreground">
                          Ordered, lightweight questions for this workflow.
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => addQuestion(draft, setDraft)}
                      >
                        <Plus className="h-3.5 w-3.5" /> Add question
                      </Button>
                    </div>
                    {draft.receptionist.qualificationQuestions.length === 0 && (
                      <EmptyInline>No qualification questions configured.</EmptyInline>
                    )}
                    {draft.receptionist.qualificationQuestions.map((question, index) => (
                      <QuestionEditor
                        key={question.id}
                        question={question}
                        index={index}
                        total={draft.receptionist.qualificationQuestions.length}
                        onChange={(patch) => updateQuestion(draft, setDraft, question.id, patch)}
                        onMove={(direction) => moveQuestion(draft, setDraft, index, direction)}
                        onRemove={() => removeQuestion(draft, setDraft, question.id)}
                      />
                    ))}
                  </div>
                </Section>
              </TabsContent>

              <TabsContent value="scheduling" className="mt-5">
                <Section
                  title="Temporary availability"
                  description="Offer times only inside up to three explicitly approved windows."
                >
                  <div className="space-y-3 md:col-span-2">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-muted-foreground">
                        Without a window, the receptionist collects preferred days and times for
                        human follow-up.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={draft.scheduling.availabilityWindows.length >= 3}
                        onClick={() => addAvailability(draft, setDraft)}
                      >
                        <CalendarClock className="h-3.5 w-3.5" /> Add window
                      </Button>
                    </div>
                    {draft.scheduling.availabilityWindows.length === 0 && (
                      <EmptyInline>No approved availability windows.</EmptyInline>
                    )}
                    {draft.scheduling.availabilityWindows.map((window, index) => (
                      <AvailabilityEditor
                        key={window.id}
                        value={window}
                        index={index}
                        users={users}
                        onChange={(patch) => updateAvailability(draft, setDraft, window.id, patch)}
                        onRemove={() => removeAvailability(draft, setDraft, window.id)}
                      />
                    ))}
                  </div>
                </Section>
              </TabsContent>

              <TabsContent value="notifications" className="mt-5">
                <Section
                  title="Notifications"
                  description="Choose which outcomes should alert the assigned user."
                >
                  <ToggleField
                    label="Call completes"
                    checked={draft.notifications.callCompleted}
                    onChange={(checked) => updateGroup('notifications', { callCompleted: checked })}
                  />
                  <ToggleField
                    label="Lead is qualified"
                    checked={draft.notifications.leadQualified}
                    onChange={(checked) => updateGroup('notifications', { leadQualified: checked })}
                  />
                  <ToggleField
                    label="Appointment is scheduled"
                    checked={draft.notifications.appointmentScheduled}
                    onChange={(checked) =>
                      updateGroup('notifications', { appointmentScheduled: checked })
                    }
                  />
                  <ToggleField
                    label="Call fails"
                    checked={draft.notifications.callFailed}
                    onChange={(checked) => updateGroup('notifications', { callFailed: checked })}
                  />
                  <Field label="Notification email" className="md:col-span-2">
                    <Input
                      type="email"
                      value={draft.notifications.email}
                      placeholder={draft.general.assignedPortalUserEmail || 'owner@example.com'}
                      onChange={(event) =>
                        updateGroup('notifications', { email: event.target.value })
                      }
                    />
                  </Field>
                </Section>
              </TabsContent>

              <TabsContent value="field_mapping" className="mt-5">
                <Section
                  title="Form field mapping"
                  description="Map incoming form fields to stable LeadLoop lead fields."
                >
                  <div className="space-y-3 md:col-span-2">
                    {draft.fieldMappings.map((mapping, index) => (
                      <div
                        key={`${index}-${mapping.targetField}`}
                        className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-center"
                      >
                        <Input
                          value={mapping.sourceField}
                          placeholder="Incoming field"
                          onChange={(event) =>
                            updateMapping(draft, setDraft, index, {
                              sourceField: event.target.value,
                            })
                          }
                        />
                        <span className="text-center text-muted-foreground">→</span>
                        <Select
                          value={mapping.targetField}
                          onValueChange={(value) =>
                            updateMapping(draft, setDraft, index, { targetField: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_FIELDS.map((field) => (
                              <SelectItem key={field} value={field}>
                                {field}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label="Remove mapping"
                          onClick={() => removeMapping(draft, setDraft, index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          fieldMappings: [
                            ...current.fieldMappings,
                            { sourceField: '', targetField: 'lead.message' },
                          ],
                        }))
                      }
                    >
                      <Plus className="h-3.5 w-3.5" /> Add mapping
                    </Button>
                  </div>
                </Section>
              </TabsContent>
            </Tabs>
          </fieldset>
        </CardContent>
      </Card>

      <div className="sticky bottom-3 flex flex-col gap-2 rounded-lg border border-border bg-card/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs">
          {errorMessage && <span className="text-destructive">{errorMessage}</span>}
          {saved && !errorMessage && (
            <span className="inline-flex items-center gap-1 text-success">
              <CheckCircle2 className="h-3.5 w-3.5" /> Settings saved
            </span>
          )}
          {!canManage && (
            <span className="text-muted-foreground">You have view-only access to this Switch.</span>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setDraft(structuredClone(item.settings))}
            disabled={!canManage || saveMutation.isPending}
          >
            Discard
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!canManage || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-4">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({
  label,
  hint,
  className = '',
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${className}`}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

function ToggleField({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {description && <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function EmptyInline({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function QuestionEditor({
  question,
  index,
  total,
  onChange,
  onMove,
  onRemove,
}: {
  question: QualificationQuestion;
  index: number;
  total: number;
  onChange: (patch: Partial<QualificationQuestion>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Question {index + 1}
        </span>
        <div className="flex gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button type="button" size="icon" variant="ghost" onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Question">
          <Input
            value={question.label}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </Field>
        <Field label="Response type">
          <Select
            value={question.responseType}
            onValueChange={(value) =>
              onChange({ responseType: value as QualificationQuestion['responseType'] })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="yes_no">Yes / no</SelectItem>
              <SelectItem value="single_select">Single select</SelectItem>
              <SelectItem value="number">Number</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {question.responseType === 'single_select' && (
          <Field label="Options" hint="Comma-separated">
            <Input
              value={question.options.join(', ')}
              onChange={(event) => onChange({ options: commaList(event.target.value) })}
            />
          </Field>
        )}
        <Field label="Lead field mapping">
          <Input
            value={question.leadFieldMapping}
            placeholder="lead.qualification_detail"
            onChange={(event) => onChange({ leadFieldMapping: event.target.value })}
          />
        </Field>
        <ToggleField
          label="Required"
          checked={question.required}
          onChange={(required) => onChange({ required })}
        />
      </div>
    </div>
  );
}

function AvailabilityEditor({
  value,
  index,
  users,
  onChange,
  onRemove,
}: {
  value: AvailabilityWindow;
  index: number;
  users: PortalUserOption[];
  onChange: (patch: Partial<AvailabilityWindow>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Window {index + 1}
        </span>
        <Button type="button" size="icon" variant="ghost" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Start">
          <Input
            type="datetime-local"
            value={value.startAt}
            onChange={(event) => onChange({ startAt: event.target.value })}
          />
        </Field>
        <Field label="End">
          <Input
            type="datetime-local"
            value={value.endAt}
            onChange={(event) => onChange({ endAt: event.target.value })}
          />
        </Field>
        <Field label="Timezone">
          <Input
            value={value.timezone}
            onChange={(event) => onChange({ timezone: event.target.value })}
          />
        </Field>
        <Field label="Meeting duration (minutes)">
          <Input
            type="number"
            min={15}
            step={15}
            value={value.meetingDurationMinutes}
            onChange={(event) =>
              onChange({ meetingDurationMinutes: numberInput(event.target.value, 15) })
            }
          />
        </Field>
        <Field label="Assigned user" className="md:col-span-2">
          <Select
            value={value.assignedPortalUserId || undefined}
            onValueChange={(assignedPortalUserId) => onChange({ assignedPortalUserId })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a user" />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}

const LEAD_FIELDS = [
  'lead.name',
  'lead.first_name',
  'lead.last_name',
  'lead.fallback_identifier',
  'lead.email',
  'lead.phone',
  'lead.message',
  'lead.service',
];

function issueTab(issue: SwitchConfigurationIssue | undefined): SettingsTab | undefined {
  if (!issue) return undefined;
  if (issue.section === 'platform') return 'trigger';
  if (issue.section === 'triage') return 'general';
  return issue.section;
}

function numberInput(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function commaList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}
function lineList(value: string): string[] {
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function addQuestion(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: (
    value:
      | AutomatedFormFollowUpCallSettings
      | ((current: AutomatedFormFollowUpCallSettings) => AutomatedFormFollowUpCallSettings),
  ) => void,
) {
  const question: QualificationQuestion = {
    id: uniqueId('question'),
    label: '',
    responseType: 'text',
    required: false,
    options: [],
    leadFieldMapping: '',
  };
  setSettings({
    ...settings,
    receptionist: {
      ...settings.receptionist,
      qualificationQuestions: [...settings.receptionist.qualificationQuestions, question],
    },
  });
}
function updateQuestion(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
  id: string,
  patch: Partial<QualificationQuestion>,
) {
  setSettings({
    ...settings,
    receptionist: {
      ...settings.receptionist,
      qualificationQuestions: settings.receptionist.qualificationQuestions.map((question) =>
        question.id === id ? { ...question, ...patch } : question,
      ),
    },
  });
}
function removeQuestion(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
  id: string,
) {
  setSettings({
    ...settings,
    receptionist: {
      ...settings.receptionist,
      qualificationQuestions: settings.receptionist.qualificationQuestions.filter(
        (question) => question.id !== id,
      ),
    },
  });
}
function moveQuestion(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
  index: number,
  direction: -1 | 1,
) {
  const questions = [...settings.receptionist.qualificationQuestions];
  const target = index + direction;
  if (target < 0 || target >= questions.length) return;
  [questions[index], questions[target]] = [questions[target]!, questions[index]!];
  setSettings({
    ...settings,
    receptionist: { ...settings.receptionist, qualificationQuestions: questions },
  });
}

function addAvailability(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
) {
  const window: AvailabilityWindow = {
    id: uniqueId('window'),
    startAt: '',
    endAt: '',
    timezone: settings.general.timezone,
    meetingDurationMinutes: 30,
    assignedPortalUserId: settings.general.assignedPortalUserId,
  };
  setSettings({
    ...settings,
    scheduling: {
      ...settings.scheduling,
      availabilityWindows: [...settings.scheduling.availabilityWindows, window].slice(0, 3),
    },
  });
}
function updateAvailability(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
  id: string,
  patch: Partial<AvailabilityWindow>,
) {
  setSettings({
    ...settings,
    scheduling: {
      ...settings.scheduling,
      availabilityWindows: settings.scheduling.availabilityWindows.map((window) =>
        window.id === id ? { ...window, ...patch } : window,
      ),
    },
  });
}
function removeAvailability(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
  id: string,
) {
  setSettings({
    ...settings,
    scheduling: {
      ...settings.scheduling,
      availabilityWindows: settings.scheduling.availabilityWindows.filter(
        (window) => window.id !== id,
      ),
    },
  });
}

function updateMapping(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
  index: number,
  patch: Partial<AutomatedFormFollowUpCallSettings['fieldMappings'][number]>,
) {
  setSettings({
    ...settings,
    fieldMappings: settings.fieldMappings.map((mapping, current) =>
      current === index ? { ...mapping, ...patch } : mapping,
    ),
  });
}
function removeMapping(
  settings: AutomatedFormFollowUpCallSettings,
  setSettings: Parameters<typeof addQuestion>[1],
  index: number,
) {
  setSettings({
    ...settings,
    fieldMappings: settings.fieldMappings.filter((_, current) => current !== index),
  });
}
function uniqueId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function IncludedSwitchHeader({ item, onBack }: Pick<SwitchSettingsProps, 'item' | 'onBack'>) {
  return (
    <div>
      <Button variant="ghost" size="sm" className="-ml-3 mb-2" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" /> Back to Switchboard
      </Button>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-semibold">{item.definition.name}</h2>
        <Badge variant="success">Included · Active</Badge>
      </div>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{item.definition.description}</p>
    </div>
  );
}

function AnalyzeEndOfCallSettingsView({ item, onBack }: SwitchSettingsProps) {
  const settings = item.settings as AnalyzeEndOfCallSettings;
  return (
    <div className="space-y-5">
      <IncludedSwitchHeader item={item} onBack={onBack} />
      <Card>
        <CardHeader><CardTitle className="text-base">Analysis returned after completed calls</CardTitle></CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            This system Switch describes the existing Vapi report. It does not change Vapi configuration and cannot be paused.
          </p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {settings.capabilities.map((capability) => (
              <li key={capability} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                {capability.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Intelligent Form Triage is Always On and has nothing to configure, so this
 * page is only an account of what it did: the current-month counts, read from
 * the Form Entries themselves.
 */
function IntelligentFormTriageView({ item, onBack }: SwitchSettingsProps) {
  const usage = item.usage.formTriage;
  return (
    <div className="space-y-5">
      <IncludedSwitchHeader item={item} onBack={onBack} />
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryMetric label="Forms analyzed" value={usage?.analyzed ?? 0} />
        <SummaryMetric label="Likely service requests" value={usage?.likelyServiceRequests ?? 0} />
        <SummaryMetric label="Low-value submissions separated" value={usage?.lowValueSeparated ?? 0} />
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">How entries are classified</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Every Form Entry is reviewed as it arrives and stored with one classification.
            Likely Service Request and Needs Review stay in the actionable feed; Low Value moves
            to its own tab. Nothing is deleted — the All view keeps every submission searchable.
          </p>
          <p>
            The message and the intent it expresses decide the classification. A nonlocal phone
            number never makes an otherwise legitimate request Low Value, and anything uncertain
            stays on Needs Review. You can change any entry&rsquo;s classification from its details.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function IncomingCallTriageSettingsView({ item, onBack, canManage }: SwitchSettingsProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<IncomingCallTriageSettings>(() =>
    normalizeIncomingCallTriageSettings(item.settings),
  );
  useEffect(() => setDraft(normalizeIncomingCallTriageSettings(item.settings)), [item.id, item.settings]);
  const save = useMutation({
    mutationFn: () => switchboardApi.saveSettings(item.definition.key, draft),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
  const axes = [
    ['purposes', 'Call purposes', draft.purposes],
    ['callerRelationships', 'Caller relationships', draft.callerRelationships],
    ['leadEligibilityOptions', 'Lead eligibility', draft.leadEligibilityOptions],
  ] as const;

  return (
    <div className="space-y-5">
      <IncludedSwitchHeader item={item} onBack={onBack} />
      <Card>
        <CardContent className="space-y-6 p-5">
          <p className="rounded-md border bg-muted/40 p-3 text-sm">
            These are independent fields. An Existing Contact can still be an Eligible Lead when the call is for a new paid opportunity. Only Lead eligibility controls LeadLoop.
          </p>
          {axes.map(([axis, title, options]) => (
            <section key={axis}>
              <h3 className="mb-2 font-medium">{title}</h3>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <div key={option.key} className="grid gap-2 rounded-md border p-3 md:grid-cols-[auto_220px_1fr]">
                    <input
                      type="checkbox"
                      checked={option.enabled}
                      disabled={!canManage || (axis === 'leadEligibilityOptions' && option.key === 'eligible')}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        [axis]: current[axis].map((entry, currentIndex) => currentIndex === index ? { ...entry, enabled: event.target.checked } : entry),
                      }))}
                    />
                    <Input
                      value={option.label}
                      disabled={!canManage}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        [axis]: current[axis].map((entry, currentIndex) => currentIndex === index ? { ...entry, label: event.target.value } : entry),
                      }))}
                    />
                    <Input
                      value={option.description}
                      disabled={!canManage}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        [axis]: current[axis].map((entry, currentIndex) => currentIndex === index ? { ...entry, description: event.target.value } : entry),
                      }))}
                    />
                    <span className="md:col-start-2 md:col-span-2 text-xs text-muted-foreground">
                      Examples: {option.examples.join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
          <section>
            <h3 className="mb-2 font-medium">Low Value call purposes</h3>
            <div className="flex flex-wrap gap-3">
              {draft.purposes.filter((option) => option.enabled && LOW_VALUE_CALL_PURPOSE_KEYS.includes(option.key as typeof LOW_VALUE_CALL_PURPOSE_KEYS[number])).map((option) => (
                <label key={option.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.lowValuePurposes.includes(option.key)}
                    disabled={!canManage}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      lowValuePurposes: event.target.checked
                        ? [...new Set([...current.lowValuePurposes, option.key])]
                        : current.lowValuePurposes.filter((key) => key !== option.key),
                    }))}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Only obvious solicitation, spam, or wrong-number traffic can be routed out of the Main Feed.
              Real business questions stay visible even when the caller hangs up before sharing contact details.
            </p>
          </section>
          <div className="grid gap-4 sm:grid-cols-3">
            <FallbackSelect label="Fallback purpose" value={draft.fallbackPurpose} options={draft.purposes} onChange={(value) => setDraft((current) => ({ ...current, fallbackPurpose: value }))} />
            <FallbackSelect label="Fallback caller relationship" value={draft.fallbackCallerRelationship} options={draft.callerRelationships} onChange={(value) => setDraft((current) => ({ ...current, fallbackCallerRelationship: value }))} />
            <FallbackSelect label="Fallback Lead eligibility" value={draft.fallbackLeadEligibility} options={draft.leadEligibilityOptions} onChange={(value) => setDraft((current) => ({ ...current, fallbackLeadEligibility: value }))} />
          </div>
          <Button disabled={!canManage || save.isPending} onClick={() => save.mutate()}>Save triage settings</Button>
          {save.error && <p className="text-sm text-destructive">{save.error instanceof Error ? save.error.message : 'Settings could not be saved.'}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function FallbackSelect<T extends string>({ label, value, options, onChange }: {
  label: string;
  value: T;
  options: Array<{ key: T; label: string; enabled: boolean }>;
  onChange: (value: T) => void;
}) {
  return (
    <label className="text-xs font-medium">
      {label}
      <select className="mt-1 w-full rounded-md border bg-background px-2 py-2 text-sm" value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.filter((option) => option.enabled).map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
    </label>
  );
}
